import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
    ClientInterface,
    ClientCustomRole,
    AccountDetails,
    ClientIdentifier,
    ClientIdentifierUpdate,
    ClientInformationInterface,
    ClientIdentifierType,
} from '../interfaces/client-data';
import { Observable } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class ClientsService {
    private serverUrl = 'https://' + window.location.host; // TODO Get url and version number from appsettings.
    private version = '0.5';
    private _url = (this.serverUrl + '/api/v{version}/clients/').replace('{version}', this.version);

    constructor(private http: HttpClient) {}

    getClientsData(): Observable<ClientInterface[]> {
        return this.http.get<ClientInterface[]>(this._url + 'GetClients');
    }

    getClientData(clientShortName: string): Observable<ClientInterface> {
        const params = new HttpParams().set('clientShortName', clientShortName);
        return this.http.get<ClientInterface>(this._url + 'GetClient', {
            params: params,
        });
    }

    getAuthenticationMethod(): Observable<boolean> {
        return this.http.get<boolean>(this._url + 'GetAuthenticationMethod');
    }

    getClientCustomRoles(clientShortName: string): Observable<ClientCustomRole[]> {
        const params = new HttpParams().set('clientShortName', clientShortName);

        return this.http.get<ClientCustomRole[]>(this._url + 'GetCustomRolesByShortName', {
            params: params,
        });
    }

    getCustomRoles(): Observable<ClientCustomRole[]> {
        return this.http.get<ClientCustomRole[]>(this._url + 'GetCustomRoles');
    }

    getAllClientAccountDetails(): Observable<AccountDetails[]> {
        return this.http.get<AccountDetails[]>(this._url + 'GetAllClientsAccountDetails');
    }

    // TODO: if normal and impersonation works in method getting clientKey in service don't need to pass this
    getAccountDetails(clientShortName: string): Observable<AccountDetails> {
        const params = { parameters: clientShortName };
        return this.http.post<AccountDetails>(this._url + 'GetClientAccountDetails', params);
    }

    getClientCSUrl(clientShortName: string): Observable<AccountDetails> {
        const params = new HttpParams().set('clientShortName', clientShortName);
        return this.http.get<AccountDetails>(this._url + 'GetClientCSUrl', {
            params: params,
        });
    }

    getClientIdentifierTypes(): Observable<ClientIdentifierType[]> {
        return this.http.get<ClientIdentifierType[]>(this._url + 'GetClientIdentifierTypes');
    }

    getClientIdentifierRecords(clientShortName: string): Observable<ClientIdentifier[]> {
        const params = new HttpParams().set('clientShortName', clientShortName);
        return this.http.get<ClientIdentifier[]>(this._url + 'GetClientIdentifierRecords', {
            params: params,
        });
    }

    insertClientIdentifier(details: ClientIdentifier, shortName: string): Observable<any> {
        const params = {
            data: details,
            shortName: shortName,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(this._url + 'InsertClientIdentifier', params).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    updateClientIdentifier(details: ClientIdentifierUpdate, shortName: string): Observable<any> {
        const params = {
            data: details,
            shortName: shortName,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(this._url + 'UpdateClientIdentifier', params).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    deleteClientIdentifier(details: ClientIdentifier, shortName: string): Observable<any> {
        const params = {
            data: details,
            shortName: shortName,
        };
        return new Observable((subscriber) => {
            this.http.post<any>(this._url + 'DeleteClientIdentifier', params).subscribe(
                () => {
                    subscriber.next(true);
                },
                (response) => {
                    subscriber.error(response);
                }
            );
        });
    }

    upsertClient(model: ClientInformationInterface): Observable<any> {
        let params = { parameters: JSON.stringify(model) };
        return this.http.post<any>(this._url + 'UpsertClient', params);
    }

    getSuggestedClientKey(): Observable<any> {
        return this.http.get(`${this._url}clientkey/suggested`);
    }

    isClientShortNameUnique(clientShortName: string): Observable<any> {
        const params = new HttpParams().set('clientShortName', clientShortName);

        return this.http.get(`${this._url}IsClientShortNameUnique`, {
            params: params,
        });
    }

    isClientKeyUnique(clientKey: number): Observable<any> {
        const params = new HttpParams().set('clientKey', clientKey.toString());

        return this.http.get(`${this._url}IsClientKeyUnique`, {
            params: params,
        });
    }

    getClientIdentifierType(): Observable<any> {
        return this.http.get(`${this._url}GetClientIdentifierType`);
    }

    getClientIdentifierTypeByClientKey(clientKey: number): Observable<any> {
        const params = new HttpParams().set('clientKey', clientKey.toString());

        return this.http.get(`${this._url}GetClientIdentifierTypeByClientKey`, {
            params: params,
        });
    }

    getClientIdentifierTypeSelectNonEntryByClientKey(clientKey: number): Observable<any> {
        const params = new HttpParams().set('clientKey', clientKey.toString());

        return this.http.get(`${this._url}GetClientIdentifierTypeSelectNonEntryByClientKey`, {
            params: params,
        });
    }
}
